import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Container, Grid, Paper, Chip, useMediaQuery, Card, CardContent, alpha } from '@mui/material';
import { ArrowForward, Security, AccountBalance, Payments, MobileFriendly, PlayArrow, Pause } from '@mui/icons-material';
import { motion } from "framer-motion";
import "slick-carousel/slick/slick.css"; 
import "slick-carousel/slick/slick-theme.css";

// Import all images
import bankImage from '../images/b.jpg';
import appScreenshot from '../images/s.jpg';
import platinumCard from '../images/sm.jpeg';
import silverCard from '../images/sm.jpeg';
import whiteCard from '../images/wc5.jpeg'; // Changed from goldCard to whiteCard
import diamondCard from '../images/dm.jpeg';
import j1 from '../images/j1.jpg';
import j2 from '../images/j2.jpg';
import j3 from '../images/j3.jpg';
import j4 from '../images/j4.jpg';
import j5 from '../images/j5.jpg';
import j6 from '../images/j6.jpg';
import j7 from '../images/j7.jpg';

// Import video
import vdVideo from '../images/vd.mp4'; // Adjust path if needed

// Color palette
const colors = {
  primary: '#0d2818', // Darker green for more sophistication
  secondary: '#4caf50', // Vibrant green
  accent: '#ffc107', // Gold accent
  lightBg: '#f8fffb', // Very light green
  textDark: '#132714',
  textLight: '#ffffff'
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5
    }
  }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8 } }
};

const slideInFromLeft = {
  hidden: { x: -50, opacity: 0 },
  visible: { x: 0, opacity: 1, transition: { duration: 0.6 } }
};

const slideInFromRight = {
  hidden: { x: 50, opacity: 0 },
  visible: { x: 0, opacity: 1, transition: { duration: 0.6 } }
};

const Home = () => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const [isPlaying, setIsPlaying] = useState(true);
  const heroImages = [j1, j2, j3, j4, j5, j6, j7];

  const services = [
    {
      icon: <Security sx={{ fontSize: 50, color: colors.accent }} />,
      title: 'Secure Banking',
      content: 'Advanced security measures to protect your finances 24/7'
    },
    {
      icon: <AccountBalance sx={{ fontSize: 50, color: colors.accent }} />,
      title: 'Business Loans',
      content: 'Specialized financing options for businesses and individuals'
    },
    {
      icon: <Payments sx={{ fontSize: 50, color: colors.accent }} />,
      title: 'Easy Payments',
      content: 'Streamlined payment systems for all your business needs'
    },
    {
      icon: <MobileFriendly sx={{ fontSize: 50, color: colors.accent }} />,
      title: 'Mobile Banking',
      content: 'Bank anytime, anywhere with our advanced mobile app'
    }
  ];

  const premiumCards = [
    
    {
      icon: whiteCard, // Changed from goldCard to whiteCard
      title: 'SILVER ACCOUNT', // Updated title
      content: 'Premium services with our exclusive membership card',
      color: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)', // White gradient
      textColor: '#132714', // Dark text for contrast
      chipColor: '#f0f0f0',
      features: ['3% cashback', '24/7 concierge', 'Travel insurance']
    },
    {
      icon: diamondCard,
      title: 'GOLD ACCOUNT',
      content: 'Ultimate banking experience with diamond status',
      color: 'linear-gradient(135deg, #b9f2ff 0%, #7ec4cf 100%)',
      textColor: '#0d5661',
      chipColor: '#b9f2ff',
      features: ['7% cashback', 'Personal banker', 'Global VIP services']
    },
    {
      icon: silverCard,
      title: 'DIAMOND ACCOUNT',
      content: 'Great value with our silver account options',
      color: 'linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%)',
      textColor: '#4a4a4a',
      chipColor: '#C0C0C0',
      features: ['1.5% cashback', 'No annual fees', 'Mobile banking']
    }
    
  ];

  const newsItems = [
    {
      title: 'Mobile Banking Update',
      content: 'Enhanced features now available in our mobile banking application',
      tag: 'Mobile',
      image: <Box component="img" src={bankImage} alt="Coins" sx={{ width: '100%', height: 250, objectFit: 'cover' }} />
    },
    {
      title: 'Premium Cards Launch',
      content: 'Introducing our new platinum and diamond card options',
      tag: 'Cards',
      image: <Box component="img" src={diamondCard} alt="Diamond Card" sx={{ width: '100%', height: 250, objectFit: 'cover' }} />
    }
  ];

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <Box sx={{ overflowX: 'hidden', backgroundColor: colors.lightBg }}>
        {/* Hero Section - Completely redesigned */}
        <Box sx={{
          height: isMobile ? '90vh' : '100vh',
          minHeight: isMobile ? '600px' : '800px',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center'
        }}>
          {/* Parallax Background */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 0,
            backgroundImage: `url(${j1})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            filter: 'brightness(0.7)'
          }} />

          {/* Animated Gradient Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, rgba(19,39,20,0.85) 0%, rgba(87,166,73,0.6) 100%)',
              zIndex: 1
            }}
          />

          {/* Hero Content */}
          <Container maxWidth="lg" sx={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
            py: 6
          }}>
            <Box sx={{
              maxWidth: isMobile ? '100%' : '80%',
              textAlign: isMobile ? 'center' : 'left',
            }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                {/* Main headline - THE SAFE BANK */}
                <Typography
                  variant="h3"
                  component="h3"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    fontSize: isMobile ? '3.5rem' : '5.5rem',
                    lineHeight: 1.1,
                    letterSpacing: '0.03em',
                    color: colors.textLight,
                    mb: 2,
                    textShadow: '0 2px 10px rgba(0,0,0,0.5)',
                    textTransform: 'uppercase'
                  }}
                >
                  THE SAFE BANK
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    mb: 4,
                    fontWeight: 400,
                    color: 'rgba(255,255,255,0.9)',
                    fontSize: isMobile ? '1.4rem' : '1.8rem',
                    lineHeight: 1.6,
                    maxWidth: '500px'
                  }}
                >
                  Modern financial solutions. Secure, reliable, and built for growth.
                </Typography>

                <Box sx={{
                  display: 'flex',
                  gap: 3,
                  justifyContent: isMobile ? 'center' : 'flex-start',
                  flexWrap: 'wrap',
                  mt: 4
                }}>
                  <Button
                    component={motion.button}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                    whileHover={{
                      scale: 1.05,
                      boxShadow: `0 6px 16px ${colors.secondary}b3`
                    }}
                    whileTap={{ scale: 0.98 }}
                    variant="contained"
                    color="secondary"
                    size="large"
                    href="/personal"
                    sx={{
                      px: 6,
                      py: 2,
                      fontSize: '1.1rem',
                      borderRadius: '50px',
                      backgroundColor: colors.accent,
                      color: colors.primary,
                      fontWeight: 700,
                      boxShadow: `0 4px 20px ${colors.accent}80`,
                      '&:hover': {
                        backgroundColor: colors.accent,
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    Get Started
                  </Button>
                  <Button
                    component={motion.button}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                    whileHover={{
                      scale: 1.05,
                      backgroundColor: 'rgba(255,255,255,0.1)'
                    }}
                    whileTap={{ scale: 0.98 }}
                    variant="outlined"
                    size="large"
                    href="/about"
                    sx={{
                      px: 6,
                      py: 2,
                      fontSize: '1.1rem',
                      borderRadius: '50px',
                      borderWidth: '2px',
                      borderColor: 'white',
                      color: 'white',
                      fontWeight: 700,
                      '&:hover': {
                        borderWidth: '2px',
                        backgroundColor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    Learn More
                  </Button>
                </Box>
              </motion.div>
            </Box>

            {/* Floating Cards Animation */}
            {!isMobile && (
              <Box sx={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                width: '40%',
                height: '100%',
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center'
              }}>
                <motion.div
                  initial={{ opacity: 0, y: 100 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.8 }}
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: '80%'
                  }}
                >
                  <Box component="img"
                    src={platinumCard}
                    alt="Platinum Card"
                    sx={{
                      position: 'absolute',
                      right: '20%',
                      bottom: '30%',
                      width: '300px',
                      height: 'auto',
                      borderRadius: '16px',
                      boxShadow: '0 30px 60px rgba(0,0,0,0.3)',
                      transform: 'rotate(15deg)',
                      zIndex: 3
                    }}
                  />
                  <Box component="img"
                    src={whiteCard} // Changed from goldCard to whiteCard
                    alt="White Card"
                    sx={{
                      position: 'absolute',
                      right: '10%',
                      bottom: '40%',
                      width: '300px',
                      height: 'auto',
                      borderRadius: '16px',
                      boxShadow: '0 30px 60px rgba(0,0,0,0.3)',
                      transform: 'rotate(-5deg)',
                      zIndex: 2
                    }}
                  />
                  <Box component="img"
                    src={diamondCard}
                    alt="Diamond Card"
                    sx={{
                      position: 'absolute',
                      right: '25%',
                      bottom: '50%',
                      width: '300px',
                      height: 'auto',
                      borderRadius: '16px',
                      boxShadow: '0 30px 60px rgba(0,0,0,0.3)',
                      transform: 'rotate(5deg)',
                      zIndex: 1
                    }}
                  />
                </motion.div>
              </Box>
            )}
          </Container>
        </Box>

        {/* Services Section */}
        <Box sx={{ py: 12, backgroundColor: '#f8fffb' }}>
          <Container maxWidth="lg">
            {/* Section Heading */}
            <Box sx={{ textAlign: 'center', mb: 10 }}>
              <motion.div
                variants={itemVariants}
                viewport={{ once: true }}
              >
                <Chip
                  label="OUR SERVICES"
                  sx={{
                    backgroundColor: alpha(colors.secondary, 0.15),
                    color: colors.secondary,
                    fontWeight: 700,
                    letterSpacing: 1,
                    mb: 3,
                    fontSize: '0.85rem',
                    px: 2,
                    height: 30,
                    borderRadius: '8px'
                  }}
                />
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    color: colors.primary,
                    mb: 3,
                    fontSize: isMobile ? '2.2rem' : '3rem',
                    lineHeight: 1.2
                  }}
                >
                  Our Banking Solution.
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'text.secondary',
                    maxWidth: '720px',
                    margin: '0 auto',
                    fontSize: isMobile ? '1rem' : '1.15rem',
                    lineHeight: 1.7
                  }}
                >
                  Empowering the communities with tailored financial services to grow, sustain, and innovate.
                </Typography>
              </motion.div>
            </Box>

            {/* Cards Section */}
            <Grid container spacing={4} justifyContent="center" alignItems="stretch">
              {services.map((service, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div
                    initial={{ y: 50, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: index * 0.1,
                      duration: 0.6,
                      type: "spring",
                      stiffness: 100
                    }}
                    whileHover={{ y: -6 }}
                    style={{ height: "100%" }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        p: 4,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        textAlign: 'center',
                        borderRadius: '22px',
                        background: 'rgba(255,255,255,0.9)',
                        boxShadow: '0 8px 28px rgba(0,0,0,0.05)',
                        border: '1px solid rgba(0,0,0,0.08)',
                        transition: 'all 0.35s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          boxShadow: '0 16px 40px rgba(0,0,0,0.12)',
                          transform: 'translateY(-8px)',
                          border: `1px solid ${colors.secondary}`,
                          background: 'white'
                        }
                      }}
                    >
                      {/* Gradient Icon Badge */}
                      <Box
                        sx={{
                          mb: 3,
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${colors.secondary}, ${colors.primary})`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '2rem',
                          boxShadow: '0 6px 15px rgba(0,0,0,0.15)'
                        }}
                      >
                        {service.icon}
                      </Box>

                      {/* Title */}
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: colors.primary,
                          mb: 2
                        }}
                      >
                        {service.title}
                      </Typography>

                      {/* Description */}
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'text.secondary',
                          flexGrow: 1,
                          fontSize: '0.95rem',
                          lineHeight: 1.65
                        }}
                      >
                        {service.content}
                      </Typography>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Welcome Section with Video */}
        <Box sx={{
          py: { xs: 8, md: 12 },
          background: `linear-gradient(180deg, white 0%, ${colors.lightBg} 100%)`,
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative background pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: -100,
              right: -100,
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: alpha(colors.secondary, 0.1),
              filter: 'blur(120px)',
              zIndex: 0
            }}
          />
          <Container sx={{ position: 'relative', zIndex: 1 }}>
            <Grid container spacing={8} alignItems="center" direction={isMobile ? "column-reverse" : "row"}>

              {/* Text Content */}
              <Grid item xs={12} md={6}>
                <motion.div
                  variants={slideInFromLeft}
                  viewport={{ once: true }}
                >
                  <Typography
                    variant="h3"
                    gutterBottom
                    sx={{
                      fontWeight: 800,
                      mb: 3,
                      color: colors.primary,
                      position: 'relative',
                      display: 'inline-block',
                      background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    Welcome To Farmers Bank
                  </Typography>

                  <Typography
                    variant="body1"
                    paragraph
                    sx={{
                      fontSize: '1.15rem',
                      lineHeight: 1.8,
                      mb: 3,
                      color: 'text.secondary'
                    }}
                  >
                    As Eswatini's newest commercial bank, we merge cutting-edge digital technology with deep
                    agricultural expertise, creating financial solutions that empower farmers and agribusinesses
                    to thrive in a connected world.
                  </Typography>

                  <Typography
                    variant="body1"
                    paragraph
                    sx={{
                      fontSize: '1.15rem',
                      lineHeight: 1.8,
                      mb: 4,
                      color: 'text.secondary'
                    }}
                  >
                    Our specialized approach focuses on understanding the unique financial needs of the
                    agricultural sector — providing tailored solutions that help you grow, innovate,
                    and secure your financial future.
                  </Typography>

                  {/* Buttons */}
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Button
                      component={motion.button}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      variant="contained"
                      href="/about"
                      endIcon={<ArrowForward />}
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: '1rem',
                        borderRadius: '30px',
                        fontWeight: 600,
                        background: `linear-gradient(90deg, ${colors.secondary}, ${colors.primary})`,
                        boxShadow: `0 6px 20px ${colors.secondary}55`,
                        transition: 'all 0.35s ease',
                        '&:hover': {
                          boxShadow: `0 10px 25px ${colors.secondary}80`
                        }
                      }}
                    >
                      Our Story
                    </Button>
                    <Button
                      component={motion.button}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      variant="outlined"
                      href="/contact"
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: '1rem',
                        borderRadius: '30px',
                        fontWeight: 600,
                        borderWidth: '2px',
                        borderColor: colors.secondary,
                        color: colors.secondary,
                        transition: 'all 0.35s ease',
                        '&:hover': {
                          backgroundColor: alpha(colors.secondary, 0.08)
                        }
                      }}
                    >
                      Contact Us
                    </Button>
                  </Box>
                </motion.div>
              </Grid>

              {/* Video Section */}
              <Grid item xs={12} md={6}>
                <motion.div
                  variants={slideInFromRight}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.02 }}
                  style={{
                    position: 'relative',
                    borderRadius: '24px',
                    overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                    height: '480px',
                    transition: 'all 0.5s ease'
                  }}
                >
                  <Box
                    component="video"
                    autoPlay
                    muted
                    loop
                    playsInline
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  >
                    <source src={vdVideo} type="video/mp4" />
                    Your browser does not support the video tag.
                  </Box>

                  {/* Play/Pause Button */}
                  <Button
                    onClick={togglePlay}
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      minWidth: 'auto',
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      }
                    }}
                  >
                    {isPlaying ? <Pause /> : <PlayArrow />}
                  </Button>

                  {/* Overlay */}
                  <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 4,
                    background: `linear-gradient(to top, rgba(0,0,0,0.6), transparent)`,
                    color: 'white',
                    zIndex: 2,
                    textAlign: 'left'
                  }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      Digital Banking
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                      Experience our next-gen mobile platform with enhanced features
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Premium Cards Section - Redesigned */}
        <Box sx={{
          py: 12,
          backgroundColor: '#f9f9f9',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <motion.div
                variants={itemVariants}
                viewport={{ once: true }}
              >
                <Chip
                  label="EXCLUSIVE OFFERINGS"
                  sx={{
                    backgroundColor: colors.lightBg,
                    color: colors.secondary,
                    fontWeight: 700,
                    letterSpacing: 1,
                    mb: 3,
                    fontSize: '0.9rem',
                    height: '32px',
                    padding: '0 12px'
                  }}
                />
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    color: colors.primary,
                    mb: 2,
                    fontSize: isMobile ? '2.5rem' : '3rem'
                  }}
                >

                  Premium Banking Cards
                </Typography>
                <Typography variant="h6" sx={{
                  color: 'text.secondary',
                  maxWidth: '700px',
                  margin: '0 auto',
                  fontSize: isMobile ? '1.1rem' : '1.2rem'
                }}>
                  Elevate your banking experience with our exclusive range of premium cards, each designed to offer unique benefits tailored to your lifestyle.
                </Typography>
              </motion.div>
            </Box>

            {/* Cards Grid - Redesigned */}
            <Grid container spacing={4} justifyContent="center">
              {premiumCards.map((service, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div
                    initial={{ y: 50, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: index * 0.1,
                      duration: 0.5,
                      type: "spring",
                      stiffness: 100
                    }}
                    whileHover={{ y: -10 }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 0,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '16px',
                        background: 'white',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                        border: '1px solid rgba(0,0,0,0.05)',
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                          transform: 'translateY(-5px)'
                        }
                      }}
                    >
                      {/* Card Header */}
                      <Box sx={{
                        height: '60px',
                        background: service.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative'
                      }}>
                        <Typography variant="h6" sx={{
                          color: service.textColor, // Uses the textColor from the array
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '1px'
                        }}>
                          {service.title}
                        </Typography>
                      </Box>
                      
                      {/* Card Image */}
                      <Box sx={{
                        height: '180px',
                        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                        borderBottom: '1px solid rgba(0,0,0,0.05)'
                      }}>
                        <Box
                          component="img"
                          src={service.icon}
                          alt={service.title}
                          sx={{
                            width: '80%',
                            height: 'auto',
                            maxWidth: '250px',
                            filter: 'drop-shadow(0 5px 15px rgba(0,0,0,0.1))',
                            transition: 'transform 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.05)'
                            }
                          }}
                        />
                      </Box>
                      
                      {/* Card Content - Simplified */}
                      <Box sx={{ 
                        p: 3,
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: 'text.secondary',
                            textAlign: 'center',
                            mb: 2
                          }}
                        >
                          Premium international banking card
                        </Typography>
                        
                        <Button 
                          component={motion.button}
                          whileHover={{ 
                            scale: 1.03,
                            backgroundColor: colors.primary,
                          }}
                          variant="outlined"
                          color="primary"
                          fullWidth
                          sx={{ 
                            fontWeight: 600,
                            borderRadius: '8px',
                            py: 1.5,
                            textTransform: 'none',
                            fontSize: '0.9rem',
                            mt: 'auto'
                          }}
                        >
                          Learn More
                        </Button>
                      </Box>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
            
            {/* View All Button */}
            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <Button 
                component={motion.button}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                variant="contained"
                color="primary"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  px: 6,
                  py: 1.5,
                  borderRadius: '50px',
                  backgroundColor: colors.secondary,
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  boxShadow: `0 4px 20px ${colors.secondary}80`
                }}
              >
                View All Card Options
              </Button>
            </Box>
          </Container>
        </Box>

        {/* Stats Section */}
        <Box sx={{ 
          py: 10,
          backgroundColor: colors.primary,
          color: 'white',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '20px',
            background: `linear-gradient(to bottom, ${colors.lightBg}, ${colors.primary})`
          }
        }}>
          <Container>
            <Grid container spacing={4} justifyContent="center" textAlign="center">
              {[
                { value: '24/7', label: 'Customer Support' },
                { value: '100%', label: 'Secure Banking' }
               
              ].map((item, index) => (
                <Grid item xs={6} sm={3} key={index}>
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ 
                      type: "spring",
                      stiffness: 100,
                      delay: index * 0.1
                    }}
                  >
                    <Typography variant="h2" sx={{ fontWeight: 700, color: colors.accent, mb: 1 }}>
                      {item.value}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      {item.label}
                    </Typography>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box sx={{ 
          py: 15,
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
          color: 'white',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
            opacity: 0.3
          }
        }}>
          <Container maxWidth="md">
            <motion.div
              variants={fadeIn}
              viewport={{ once: true }}
            >
              <Typography 
                variant="h2" 
                gutterBottom
                sx={{ 
                  fontWeight: 700,
                  mb: 3,
                  fontSize: isMobile ? '2.5rem' : '3.5rem'
                }}
              >
                Ready to grow with us?
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 5,
                  opacity: 0.9,
                  fontWeight: 400,
                  fontSize: isMobile ? '1.2rem' : '1.5rem'
                }}
              >
                Join thousands of farmers and businesses already benefiting from our modern banking solutions
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button 
                  component={motion.button}
                  whileHover={{ 
                    scale: 1.05,
                    boxShadow: '0 6px 25px rgba(255,255,255,0.3)'
                  }}
                  whileTap={{ scale: 0.98 }}
                  variant="contained" 
                  color="secondary"
                  size="large"
                  href="/personal"
                  endIcon={<ArrowForward />}
                  sx={{ 
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '50px',
                    boxShadow: '0 4px 20px rgba(255,255,255,0.2)',
                    backgroundColor: colors.accent,
                    color: colors.primary,
                    fontWeight: 600,
                    transition: 'all 0.3s ease'
                  }}
                >
                  Open an Account
                </Button>
                <Button 
                  component={motion.button}
                  whileHover={{ 
                    scale: 1.05,
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }}
                  whileTap={{ scale: 0.98 }}
                  variant="outlined" 
                  size="large"
                  href="/contact"
                  sx={{ 
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '50px',
                    borderWidth: '2px',
                    borderColor: 'white',
                    color: 'white',
                    fontWeight: 600,
                    transition: 'all 0.3s ease'
                  }}
                >
                  Contact Sales
                </Button>
              </Box>
            </motion.div>
          </Container>
        </Box>

        {/* News Section */}
        <Box sx={{ 
          py: 10,
          backgroundColor: colors.lightBg
        }}>
          <Container>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <motion.div
                variants={itemVariants}
                viewport={{ once: true }}
              >
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    color: colors.secondary,
                    fontWeight: 600,
                    letterSpacing: 1,
                    mb: 1,
                    fontSize: '1.2rem'
                  }}
                >
                  STAY INFORMED
                </Typography>
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontWeight: 700,
                    color: colors.primary,
                    position: 'relative',
                    display: 'inline-block',
                    mb: 4,
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -15,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '100px',
                      height: '4px',
                      background: colors.secondary,
                      borderRadius: '2px'
                    }
                  }}
                >
                  Latest News
                </Typography>
                <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: '700px', margin: '0 auto' }}>
                  Keep up with the latest developments in banking and financial services
                </Typography>
              </motion.div>
            </Box>
            <Grid container spacing={4} justifyContent="center">
              {newsItems.map((news, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ 
                      duration: 0.6,
                      delay: index * 0.15
                    }}
                    whileHover={{ y: -5 }}
                  >
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 0,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '16px',
                        background: 'white',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                        border: '1px solid rgba(87, 166, 73, 0.1)',
                        transition: 'all 0.3s ease',
                        overflow: 'hidden',
                        '&:hover': {
                          boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
                          borderColor: 'rgba(87, 166, 73, 0.2)'
                        }
                      }}
                    >
                      {news.image}
                      <Box sx={{ p: 4, flexGrow: 1 }}>
                        <Chip 
                          label={news.tag} 
                          color="primary" 
                          sx={{ 
                            mb: 2,
                            backgroundColor: colors.lightBg,
                            color: colors.secondary,
                            fontWeight: 600
                          }} 
                        />
                        <Typography 
                          variant="h5" 
                          gutterBottom 
                          sx={{ 
                            fontWeight: 700,
                            color: colors.primary,
                            mb: 2
                          }}
                        >
                          {news.title}
                        </Typography>
                        <Typography 
                          paragraph
                          sx={{ 
                            color: 'text.secondary',
                            mb: 3,
                            fontSize: '1.1rem'
                          }}
                        >
                          {news.content}
                        </Typography>
                        <Box sx={{ textAlign: 'right' }}>
                          <Button 
                            component={motion.button}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            variant="text" 
                            color="primary"
                            endIcon={<ArrowForward />}
                            sx={{ 
                              fontWeight: 600,
                              color: colors.secondary,
                              '&:hover': {
                                backgroundColor: 'transparent',
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            Read more
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Box>
    </motion.div>
  );
};

export default Home;